import { motion } from 'framer-motion'
import { Users, Target, Award, Heart } from 'lucide-react'

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Missão',
      description: 'Transformar a gestão de escolas de natação através da tecnologia, proporcionando eficiência operacional e melhor experiência para alunos e professores.'
    },
    {
      icon: Heart,
      title: 'Visão',
      description: 'Ser a plataforma líder em soluções digitais para escolas de natação no Brasil, conectando tecnologia e esporte de forma inovadora.'
    },
    {
      icon: Award,
      title: 'Valores',
      description: 'Inovação, qualidade, transparência e compromisso com o sucesso dos nossos clientes e o desenvolvimento do esporte.'
    }
  ]

  const team = [
    {
      name: 'João Silva',
      role: 'CEO & Fundador',
      description: 'Especialista em tecnologia e apaixonado por natação, lidera a visão estratégica da empresa.',
      avatar: null
    },
    {
      name: 'Maria Santos',
      role: 'CTO',
      description: 'Responsável pelo desenvolvimento técnico e arquitetura dos sistemas.',
      avatar: null
    },
    {
      name: 'Pedro Costa',
      role: 'Head de Produto',
      description: 'Define a estratégia de produto e garante a excelência da experiência do usuário.',
      avatar: null
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="gradient-bg py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-4xl lg:text-6xl font-bold text-dark-900 mb-6">
              Sobre Nós
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed">
              Somos uma empresa especializada em soluções digitais para escolas de natação, 
              comprometida em transformar a gestão esportiva através da tecnologia.
            </p>
          </motion.div>
        </div>
      </section>

      {/* História */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-dark-900 mb-6">
                Nossa História
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Nossa empresa nasceu da paixão por natação e tecnologia. Nossa equipe 
                  identificou que as escolas de natação enfrentavam desafios comuns na 
                  gestão de alunos, pagamentos e acompanhamento de progresso.
                </p>
                <p>
                  Em 2023, desenvolvemos uma solução completa que integra todos os 
                  aspectos da gestão de uma escola de natação, desde o cadastro de 
                  alunos até o controle financeiro e avaliações de desempenho.
                </p>
                <p>
                  Hoje, nossa solução atende mais de 150 escolas em todo o Brasil, ajudando a 
                  transformar a experiência de milhares de alunos e professores.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <img 
                src="/SwimmingPool Aluno/mockup_3_telas_com_moldura.png" 
                alt="SwimmingPool Apps" 
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-dark-900 mb-4">
              Missão, Visão e Valores
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nossos princípios guiam todas as nossas decisões e o desenvolvimento 
              de nossas soluções.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="card h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 text-primary-600 rounded-2xl mb-6">
                    <value.icon className="h-8 w-8" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-dark-900 mb-4">
                    {value.title}
                  </h3>
                  
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Equipe */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-dark-900 mb-4">
              Nossa Equipe
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Conheça os profissionais que fazem nossa empresa acontecer.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="card">
                  <div className="w-24 h-24 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-primary-600">
                      {member.name.charAt(0)}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-semibold text-dark-900 mb-2">
                    {member.name}
                  </h3>
                  
                  <p className="text-primary-600 font-medium mb-4">
                    {member.role}
                  </p>
                  
                  <p className="text-gray-600">
                    {member.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Quer fazer parte da nossa história?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Entre em contato conosco e descubra como podemos ajudar sua escola 
              de natação a crescer e se modernizar.
            </p>
            <a href="/contato" className="btn-primary bg-white text-primary-600 hover:bg-gray-100">
              Fale Conosco
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About
